import { useCallback, useEffect, useRef } from 'react'
import BatteryDropdowns from './BatteryDropdowns'
import { getFieldRegistration, getFieldValidationAttributes } from '../../utils/formValidation'

function BatterySection({ register, errors, watch, onBlur, getFieldError, setValue, _deviceInfo }) {
  const formData = watch()
  const prevNumBatteriesRef = useRef()

  // Clear checkboxes when they become disabled
  useEffect(() => {
    const currentNumBatteries = formData.numBatteriesManaged
    const prevNumBatteries = prevNumBatteriesRef.current
    
    // Clear battery 2 checkboxes only when switching FROM 2 batteries TO 1 battery
    // (not on initial load when both are undefined or when staying at 1)
    if (prevNumBatteries === 'numBatteriesManaged2' && currentNumBatteries === 'numBatteriesManaged1') {
      setValue('voltConnected2', false)
      setValue('tempConnected2', false)
      setValue('shuntInstalled2', false)
      setValue('postCvSwitch2', false)
      setValue('chargeCutoffInstalled2', false)
    }
    
    // Update ref for next comparison
    prevNumBatteriesRef.current = currentNumBatteries

    // Clear battery shunt checkboxes when they become disabled due to alternator shunts
    // Battery 1 shunt disabled when Alternator 2 shunt is enabled
    if (formData.altShuntInstalled2) {
      setValue('shuntInstalled', false)
    }
    // Battery 2 shunt disabled when Alternator 1 shunt is enabled  
    if (formData.altShuntInstalled) {
      setValue('shuntInstalled2', false)
    }
  }, [formData.numBatteriesManaged, formData.altShuntInstalled, formData.altShuntInstalled2, setValue])

  const handleFieldBlur = useCallback((fieldName) => (e) => {
    if (onBlur) {
      onBlur(fieldName, e.target.value)
    }
  }, [onBlur])

  const renderFieldError = useCallback((fieldName) => {
    const error = getFieldError ? getFieldError(fieldName) : errors[fieldName]
    return error && <div className="text-danger">{error.message}</div>
  }, [getFieldError, errors])

  // Reusable input field renderer
  const renderInputField = useCallback((fieldName, label, type = 'number', tooltip = null) => (
    <div className="mb-3">
      <label htmlFor={fieldName} className="form-label">
        {label}
        {tooltip && (
          <i
            className="bi bi-info-circle ms-1"
            data-bs-toggle="popover"
            data-bs-trigger="click"
            data-bs-content={tooltip}
          ></i>
        )}
      </label>
      <input
        type={type}
        className={`form-control ${getFieldError && getFieldError(fieldName) ? 'is-invalid' : ''}`}
        id={fieldName}
        {...getFieldValidationAttributes(fieldName, formData)}
        {...register(fieldName, getFieldRegistration(fieldName, formData))}
        onBlur={handleFieldBlur(fieldName)}
      />
      {renderFieldError(fieldName)}
    </div>
  ), [formData, register, getFieldError, handleFieldBlur, renderFieldError])

  const renderBatteryFields = useCallback((suffix = '') => {
    const isBat2 = suffix === '2'
    const batNumber = isBat2 ? 'two' : 'one'
    
    return (
      <div className="mb-4">
        {/* Battery Sub-Card */}
        <div 
          className="card border-0 mb-3" 
          style={{ 
            background: isBat2 ? 'linear-gradient(to right, #fff3cd, #fdf6e3)' : 'linear-gradient(to right, #d4edda, #e8f5e8)',
            border: `2px solid ${isBat2 ? '#ffc107' : '#28a745'} !important`,
          }}
        >
          <div className="card-header fw-bold fs-6" style={{ 
            background: 'transparent', 
            borderBottom: `1px solid ${isBat2 ? '#ffc107' : '#28a745'}`,
          }}>
            <i className={`bi bi-battery-full ${isBat2 ? 'text-warning' : 'text-success'} fs-5 me-2`}></i>
            Battery bank {batNumber}
          </div>
          
          <div className="card-body" id={`bat${suffix || '1'}`}>
        
        {/* Battery Configuration Helper */}
        <div className="form-label">
          Prepopulate battery bank specifications
        </div>
        <div className="input-group mb-3">
          <BatteryDropdowns
            suffix={suffix}
            register={register}
            watch={watch}
            setValue={setValue}
            onBlur={onBlur}
            getFieldError={getFieldError}
            voltageFieldName="batteryVoltage"
          />
        </div>

        <div className="form-check form-switch mb-3">
          <input
            className="form-check-input"
            type="checkbox"
            role="switch"
            id={`voltConnected${suffix}`}
            {...register(`voltConnected${suffix}`)}
          />
          <label className="form-check-label" htmlFor={`voltConnected${suffix}`}>
            Battery voltage sense installed&nbsp;
          </label>
          <i
            className="bi bi-info-circle ms-1"
            data-bs-toggle="popover"
            data-bs-trigger="click"
            data-bs-content="If not installed, alternator voltage used"
          ></i>
        </div>

        <div className="form-check form-switch mb-3">
          <input
            className="form-check-input"
            type="checkbox"
            role="switch"
            id={`tempConnected${suffix}`}
            {...register(`tempConnected${suffix}`)}
          />
          <label className="form-check-label" htmlFor={`tempConnected${suffix}`}>
            Temperature sense installed
          </label>
        </div>

        {/* Temperature Configuration Section */}
        {formData[`tempConnected${suffix}`] && (
          <div id={`tempConnectedDiv${suffix}`}>
            {renderInputField(
              `maxChargingTemp${suffix}`,
              'Maximum charging temperature (°C)',
            )}
            {renderInputField(
              `minChargingTemp${suffix}`,
              'Minimum charging temperature (°C)',
            )}
            {renderInputField(
              `tempSlope${suffix}`,
              'Temperature compensation slope (mV/°C)',
              'number',
              'Increases the charging voltage when the temperature is below 25°C and decreases it when the temperature is above 25°C. Number must be 1 or greater, otherwise leave blank to disable setting.',
            )}
          </div>
        )}

        <div className="form-check form-switch mb-3">
          <input
            className="form-check-input"
            type="checkbox"
            role="switch"
            id={`shuntInstalled${suffix}`}
            {...register(`shuntInstalled${suffix}`)}
            disabled={
              // Battery 1 shunt disabled when Alternator 2 shunt is enabled
              (suffix === '' && formData.altShuntInstalled2) ||
              // Battery 2 shunt disabled when Alternator 1 shunt is enabled  
              (suffix === '2' && formData.altShuntInstalled)
            }
          />
          <label className="form-check-label" htmlFor={`shuntInstalled${suffix}`}>
            Battery current shunt installed
            {((suffix === '' && formData.altShuntInstalled2) || (suffix === '2' && formData.altShuntInstalled)) && (
              <span className="text-muted ms-2">(disabled - alternator shunt in use)</span>
            )}
          </label>
        </div>

        {/* Shunt Configuration Section */}
        {formData[`shuntInstalled${suffix}`] && (
          <div id={`shuntDiv${suffix}`}>
            {renderInputField(
              `shuntAmps${suffix}`,
              'Shunt current rating (amps)',
            )}
            {renderInputField(
              `shuntMv${suffix}`,
              'Shunt voltage rating (mV)',
            )}
          </div>
        )}

        {renderInputField(
          `ampHrCapacity${suffix}`,
          'Battery bank amp hours (Ah)',
        )}

        {renderInputField(
          `cRate${suffix}`,
          'Battery bank maximum charging C-rate',
          'number',
          'Maximum charging amps / battery bank capacity in decimal format. E.g., 50% C-rate is 0.5.',
        )}

        {/* Charging Profile Section */}
        <h4 className="mt-3">Charging profile</h4>
        
        <h5 className="mt-3">Bulk + Absorb</h5>
        {renderInputField(
          `cvVolts${suffix}`,
          'Charging voltage (volts)',
        )}
        {renderInputField(
          `cvMinTime${suffix}`,
          'Minimum time in charging stage (minutes)',
          'number',
          'Minimum total time in Bulk + Absorb stage. Leave blank to disable setting.',
        )}
        {renderInputField(
          `cvMaxTime${suffix}`,
          'Maximum time in charging stage (minutes)',
          'number',
          'Maximum total time in Bulk + Absorb stage. Leave blank to disable setting.',
        )}

        {/* Field Power Target vs Current Minimum based on shunt installation */}
        {!formData[`shuntInstalled${suffix}`] && 
          renderInputField(
            `cvFieldPct${suffix}`,
            'Field power target minimum until transitioning to next phase (%)',
            'number',
            'Will not be used if current shunt installed or current detected over CAN bus. This is an absolute percentage and does NOT scale with the alternator\'s Maximum field voltage (%) setting.',
          )
        }

        {renderInputField(
          `cvMinAmps${suffix}`,
          'Current minimum as % of battery bank capacity until transitioning to next phase (%)',
        )}

        <div className="form-check form-switch mb-3">
          <input
            className="form-check-input"
            type="checkbox"
            role="switch"
            id={`postCvSwitch${suffix}`}
            {...register(`postCvSwitch${suffix}`)}
          />
          <label className="form-check-label" htmlFor={`postCvSwitch${suffix}`}>
            Use additional absorb stage&nbsp;
          </label>
          <i
            className="bi bi-info-circle ms-1"
            data-bs-toggle="popover"
            data-bs-trigger="click"
            data-bs-content="This setting is rare"
          ></i>
        </div>

        {/* Absorb 2 Section */}
        {formData[`postCvSwitch${suffix}`] && (
          <div id={`postCvSwitchDiv${suffix}`}>
            <h5 className="mt-3">Absorb 2</h5>
            {renderInputField(
              `postCvVolts${suffix}`,
              'Charging voltage (volts)',
            )}
            {renderInputField(
              `postCvMinTime${suffix}`,
              'Minimum time in charging stage (minutes)',
              'number',
              'Leave blank to disable setting.',
            )}
            {renderInputField(
              `postCvMaxTime${suffix}`,
              'Maximum time in charging stage (minutes)',
              'number',
              'Leave blank to disable setting.',
            )}

            {!formData[`shuntInstalled${suffix}`] && 
              renderInputField(
                `postCvFieldPct${suffix}`,
                'Field power target minimum until transitioning to next phase (%)',
                'number',
                'This is an absolute percentage and does NOT scale with the alternator\'s Maximum field voltage (%) setting.',
              )
            }

            {renderInputField(
              `postCvMinAmps${suffix}`,
              'Current minimum as % of battery bank capacity until transitioning next phase (%)',
            )}
          </div>
        )}

        {/* Float Section */}
        <h5 className="mt-3">Float</h5>
        {renderInputField(
          `floatVolts${suffix}`,
          'Charging voltage (volts)',
        )}
        {renderInputField(
          `floatMinTime${suffix}`,
          'Minimum time in charging stage (minutes)',
          'number',
          'Leave blank to disable setting.',
        )}
        {renderInputField(
          `floatMaxTime${suffix}`,
          'Maximum time in charging stage (minutes)',
          'number',
          'Leave blank to disable setting.',
        )}
        {renderInputField(
          `floatMinVolts${suffix}`,
          'Go to previous phase if voltage drops below this minimum for 30+ seconds (volts)',
          'number',
          'Leave blank to disable setting.',
        )}

        {!formData[`shuntInstalled${suffix}`] && 
          renderInputField(
            `floatFieldPctMax${suffix}`,
            'Go to previous phase if field power target exceeds this maximum for 30+ seconds (%)',
            'number',
            'Leave blank to disable setting. This is an absolute percentage and does NOT scale with the alternator\'s Maximum field voltage (%) setting.',
          )
        }

        {renderInputField(
          `floatMaxAmpHrs${suffix}`,
          'Go to previous phase if amp hour draw exceeds this percent of battery bank capacity from beginning of charge stage (%)',
          'number',
          'Leave blank to disable setting.',
        )}

        {/* External charge cutoff */}
        <h4 className="mt-3">External charge cutoff</h4>
        <div className="form-check form-switch mb-3">
          <input
            className="form-check-input"
            type="checkbox"
            role="switch"
            id={`chargeCutoffInstalled${suffix}`}
            {...register(`chargeCutoffInstalled${suffix}`)}
          />
          <label className="form-check-label" htmlFor={`chargeCutoffInstalled${suffix}`}>
            External alternator charge cutoff installed
          </label>
        </div>

        {formData[`chargeCutoffInstalled${suffix}`] && (
          <div id={`chargeCutoffInstalledDiv${suffix}`}>
            <div className="form-label">
              Alternator charge cutoff by
              <i
                className="bi bi-info-circle ms-1"
                data-bs-toggle="popover"
                data-bs-trigger="click"
                data-bs-content="When the cutoff signal is 'High', the alternator will stop charging if the charge cutoff signal >2 volts. 'Ground' is the opposite. Best practice is Ground signal."
              ></i>
            </div>
            <div
              className="btn-group mb-3"
              role="group"
              aria-label="Charge cutoff polarity"
            >
              <input
                type="radio"
                className="btn-check"
                value="highSignal"
                id={`highSignal${suffix}`}
                {...register(`chargeCutoffPolarity${suffix}`, getFieldRegistration(`chargeCutoffPolarity${suffix}`, formData))}
              />
              <label className="btn btn-outline-primary" htmlFor={`highSignal${suffix}`}>
                High signal
              </label>
              <input
                type="radio"
                className="btn-check"
                value="lowSignal"
                id={`lowSignal${suffix}`}
                {...register(`chargeCutoffPolarity${suffix}`, getFieldRegistration(`chargeCutoffPolarity${suffix}`, formData))}
              />
              <label className="btn btn-outline-primary" htmlFor={`lowSignal${suffix}`}>
                Ground signal
              </label>
            </div>
            {renderFieldError(`chargeCutoffPolarity${suffix}`)}
          </div>
        )}
        
        </div>
      </div>
    </div>
    )
  }, [formData, register, getFieldError, renderFieldError, renderInputField, watch, setValue, onBlur])

  return (
    <div 
      className="card shadow-sm border-0 mb-4" 
      style={{ 
        background: 'linear-gradient(to right, #ffffff, #f8f9fa)',
        transition: 'all 0.3s ease',
      }}
    >
      {/* Card Header - Modern Design */}
      <div className="card-header fw-bold border-0 fs-5">
        <i className="bi bi-battery-full text-success fs-3 me-2" aria-hidden="true"></i>
        <span>Batteries</span>
      </div>
      
      {/* Card Body */}
      <div className="card-body">
        {/* Battery Bank One */}
        {renderBatteryFields('')}

        {/* Battery Bank Two (only when user selects "Two" in battery bank selection) */}
        {formData.numBatteriesManaged === 'numBatteriesManaged2' && renderBatteryFields('2')}
      </div>
    </div>
  )
}

export default BatterySection
